import type { Attribute } from "@cleanbudget/contract/attribute";
import type { Holder } from "@cleanbudget/contract/holder";
import type { Fit, VariantFit } from "@cleanbudget/contract/product";
import { Field, FieldDescription, FieldLabel } from "@cleanbudget/ui/components/field";
import { NativeSelect, NativeSelectOption } from "@cleanbudget/ui/components/native-select";
import { cn } from "@cleanbudget/ui/lib/utils";
import { IconCheck } from "@tabler/icons-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { HelpButton } from "@/components/help/help-button";
import { orpc } from "@/lib/orpc";

/**
 * The building blocks for where a product sits in the shop (#160): chips to switch groups and
 * attribute values on and off, and the fit per holder. The product's card (`CatalogSection`) and a
 * variant's side panel both use them; the lists come from the groups', attributes' and holders'
 * own queries, which the editor's loader fetches.
 */

export const categoriesQuery = () => orpc.category.list.queryOptions();
export const attributesQuery = () => orpc.attribute.list.queryOptions();
export const holdersQuery = () => orpc.holder.list.queryOptions();
export const productLinesQuery = () => orpc.productLine.list.queryOptions();

/** `ids` with `id` switched on or off. */
export const toggled = (ids: readonly string[], id: string, on: boolean): string[] =>
	on ? [...ids.filter((candidate) => candidate !== id), id] : ids.filter((candidate) => candidate !== id);

/** A set of chips that each switch on and off (`aria-pressed`), such as the groups or a value list. */
export function ToggleChips({
	items,
	selected,
	onToggle,
	label,
	disabled,
}: {
	items: readonly { readonly id: string; readonly name: string }[];
	selected: readonly string[];
	onToggle: (id: string, on: boolean) => void;
	/** The group's accessible name. */
	label: string;
	disabled?: boolean;
}) {
	return (
		// biome-ignore lint/a11y/useSemanticElements: a group of toggle buttons, not a fieldset of inputs
		<div role="group" aria-label={label} className="flex flex-wrap gap-2">
			{items.map((item) => {
				const on = selected.includes(item.id);
				return (
					<button
						key={item.id}
						type="button"
						aria-pressed={on}
						disabled={disabled}
						onClick={() => onToggle(item.id, !on)}
						className={cn(
							"inline-flex min-h-8 items-center gap-1 rounded-full px-3 text-sm ring-1 transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-50",
							on
								? "bg-primary text-primary-foreground ring-primary"
								: "bg-background text-foreground ring-foreground/15 hover:bg-muted",
						)}
					>
						{on ? <IconCheck className="size-3.5" aria-hidden /> : null}
						{item.name}
					</button>
				);
			})}
		</div>
	);
}

/** Per attribute with values, its values as chips; `selected` are the value ids that are on. */
export function AttributeChips({
	attributes,
	selected,
	onChange,
	disabled,
}: {
	attributes: readonly Attribute[];
	selected: readonly string[];
	onChange: (attributeValueIds: string[]) => void;
	disabled?: boolean;
}) {
	const { t } = useTranslation("product");
	return (
		<div className="flex flex-col gap-4">
			{attributes
				.filter((attribute) => attribute.values.length > 0)
				.map((attribute) => (
					<div key={attribute.id} className="flex flex-col gap-2">
						<span className="text-sm font-medium">
							{attribute.name}
							{attribute.isSize ? (
								<span className="ml-2 text-xs font-normal text-muted-foreground">
									{t("catalog.sizeHint")}
								</span>
							) : null}
						</span>
						<ToggleChips
							label={attribute.name}
							items={attribute.values}
							selected={selected}
							disabled={disabled}
							onToggle={(id, on) => {
								// The size attribute has one value at most: choosing one replaces the other.
								const others = attribute.isSize
									? selected.filter((chosen) => !attribute.values.some((value) => value.id === chosen))
									: selected;
								onChange(toggled(on ? others : selected, id, on));
							}}
						/>
					</div>
				))}
		</div>
	);
}

/** The fit per holder, each a choice of unknown, "past op" and "niet voor". */
export function FitFields({
	holders,
	fits,
	onChange,
	disabled,
}: {
	holders: readonly Holder[];
	fits: readonly VariantFit[];
	onChange: (fits: VariantFit[]) => void;
	disabled?: boolean;
}) {
	const { t } = useTranslation("product");
	const id = useId();
	const set = (holderId: string, fit: Fit | "unknown") => {
		const others = fits.filter((candidate) => candidate.holderId !== holderId);
		onChange(fit === "unknown" ? others : [...others, { holderId, fit }]);
	};
	return (
		<fieldset className="flex flex-col gap-2">
			<legend className="mb-1 flex items-center gap-1 text-sm font-medium">
				{t("catalog.fit")}
				<HelpButton topic="product:fit" />
			</legend>
			<FieldDescription>{t("catalog.fitDescription")}</FieldDescription>
			<div className="flex flex-col gap-3">
				{holders.map((holder) => {
					const current = fits.find((fit) => fit.holderId === holder.id)?.fit ?? "unknown";
					return (
						<Field key={holder.id} className="grid grid-cols-1 items-center gap-1.5 sm:grid-cols-[1fr_11rem]">
							<FieldLabel htmlFor={`${id}-${holder.id}`} className="font-normal">
								{holder.name}
							</FieldLabel>
							<NativeSelect
								id={`${id}-${holder.id}`}
								value={current}
								disabled={disabled}
								onChange={(event) => set(holder.id, event.target.value as Fit | "unknown")}
							>
								<NativeSelectOption value="unknown">{t("catalog.fitUnknown")}</NativeSelectOption>
								<NativeSelectOption value="fits">{t("catalog.fitFits")}</NativeSelectOption>
								<NativeSelectOption value="notFor">{t("catalog.fitNotFor")}</NativeSelectOption>
							</NativeSelect>
						</Field>
					);
				})}
			</div>
		</fieldset>
	);
}
